import React, { useState, useEffect, useRef } from 'react';
import { tabs } from '@/components/editorial/hero/IconTabs';
import VideoBento from '@/components/editorial/hero/VideoBento';
import { useTheme } from '@/contexts/ThemeContext';

const FeaturesHeroBento: React.FC = () => {
  const [activeTab, setActiveTab] = useState(0);
  const [videoProgress, setVideoProgress] = useState(0);
  const intervalRef = useRef<number | null>(null);
  const { theme } = useTheme();

  // Auto-cycle through tabs
  useEffect(() => {
    const startInterval = () => {
      intervalRef.current = window.setInterval(() => {
        setVideoProgress((prev) => {
          if (prev >= 100) {
            setActiveTab((currentTab) => (currentTab + 1) % tabs.length);
            return 0;
          }
          return prev + 2;
        });
      }, 100);
    };

    startInterval();

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, []);

  const handleTabClick = (index: number) => {
    setActiveTab(index);
    setVideoProgress(0);
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }
    intervalRef.current = window.setInterval(() => {
      setVideoProgress((prev) => {
        if (prev >= 100) {
          setActiveTab((currentTab) => (currentTab + 1) % tabs.length);
          return 0;
        }
        return prev + 2;
      });
    }, 100);
  };

  const activeTabData = tabs[activeTab];

  return (
    <div className="relative w-full h-[420px] sm:h-[500px] lg:h-[560px] flex items-center justify-start overflow-visible">
      <div className="w-[160%] sm:w-[150%] lg:w-[180%] lg:min-w-[850px]">
        <div className="relative">
          {/* Glow/shadow effect behind the card */}
          <div 
            className="absolute -inset-4 rounded-2xl blur-2xl opacity-40"
            style={{
              background: theme === 'dark' 
                ? 'radial-gradient(ellipse at center, rgba(255,255,255,0.15) 0%, transparent 70%)'
                : 'radial-gradient(ellipse at center, rgba(0,0,0,0.12) 0%, transparent 70%)',
            }}
          />
          <div 
            className="relative p-3 transition-colors duration-300 overflow-hidden"
            style={{ 
              backgroundColor: theme === 'dark' ? '#1A1A1A' : '#FFFFFF',
              border: `1px solid ${theme === 'dark' ? '#333333' : '#E0E0E0'}`, 
              borderRadius: '12px',
              boxShadow: theme === 'dark'
                ? '0 25px 50px -12px rgba(0,0,0,0.5), 0 12px 24px -8px rgba(0,0,0,0.3)'
                : '0 25px 50px -12px rgba(0,0,0,0.15), 0 12px 24px -8px rgba(0,0,0,0.08)',
            }}
          >
            {/* Compact Icon Tabs - scrollable */}
            <div className="flex items-center gap-2 mb-3">
              <div className="flex items-center gap-0.5 overflow-x-auto scrollbar-hide">
                {tabs.map((tab, index) => {
                  const IconComponent = tab.icon;
                  return (
                    <button
                      key={index}
                      onClick={() => handleTabClick(index)}
                      className="relative p-1.5 rounded-md transition-colors flex-shrink-0"
                      aria-label={`View ${tab.title} feature`}
                      style={{
                        backgroundColor: activeTab === index 
                          ? (theme === 'dark' ? '#333333' : '#F5F5F5')
                          : 'transparent'
                      }}
                    >
                      <IconComponent 
                        className="w-3.5 h-3.5" 
                        style={{ 
                          color: activeTab === index 
                            ? (theme === 'dark' ? '#FFFFFF' : '#111111')
                            : (theme === 'dark' ? '#666666' : '#999999')
                        }} 
                      />
                      {activeTab === index && (
                        <div 
                          className="absolute bottom-0 left-0 h-0.5 transition-all"
                          style={{ 
                            width: `${videoProgress}%`,
                            backgroundColor: theme === 'dark' ? '#FFFFFF' : '#111111'
                          }}
                        />
                      )}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Active tab title - compact */}
            <div 
              className="flex items-center gap-1.5 mb-2 px-1"
              style={{ fontSize: '11px' }}
            >
              <span 
                className="font-semibold"
                style={{ color: theme === 'dark' ? '#FFFFFF' : '#111111' }}
              >
                {activeTabData.title}
              </span>
              <span style={{ color: theme === 'dark' ? '#999999' : '#666666' }}>
                {activeTabData.description}
              </span>
            </div>
            
            {/* Horizontal divider line */}
            <div 
              className="h-px w-full mb-3"
              style={{ 
                backgroundColor: theme === 'dark' ? '#333333' : '#E0E0E0'
              }}
            />
            
            <div className="max-h-[340px] sm:max-h-[420px] lg:max-h-[480px] overflow-hidden rounded-lg">
              <VideoBento activeTab={activeTab} />
            </div>
          </div>
          
          {/* White fade overlay - positioned inside border radius */}
          <div 
            className="absolute bottom-[13px] left-[13px] right-[13px] h-16 pointer-events-none"
            style={{
              background: theme === 'dark'
                ? 'linear-gradient(to top, rgba(26,26,26,1) 0%, rgba(26,26,26,0) 100%)'
                : 'linear-gradient(to top, rgba(255,255,255,1) 0%, rgba(255,255,255,0) 100%)',
              borderBottomLeftRadius: '8px',
              borderBottomRightRadius: '8px',
            }}
          />
          
          {/* Right fade overlay */}
          <div 
            className="absolute top-[13px] bottom-[13px] right-[13px] w-12 pointer-events-none"
            style={{
              background: theme === 'dark'
                ? 'linear-gradient(to left, rgba(26,26,26,1) 0%, rgba(26,26,26,0) 100%)'
                : 'linear-gradient(to left, rgba(255,255,255,1) 0%, rgba(255,255,255,0) 100%)',
              borderTopRightRadius: '8px',
              borderBottomRightRadius: '8px',
            }}
          />
        </div>
      </div>
    </div>
  );
};

export default FeaturesHeroBento;
